import { cn } from "../../lib/utils";
import { textBlue, semiBoldText, tertiaryText, smallTextSize, regularText, boldText, textDarkGreen, primaryText, mediumText, lightBlueBG, roundedLG } from "../../styling/stylingHelper";
import { ReactNode } from "react";
import { currencyFormatter, hasAnyInvoiceLogo } from "../../services/helperService";
import { formatCategoryName } from "../orderhistory/sections/FilterControls";
import { getCategoryStyles, getSubCategoryStyles, getSubCategoryDisplayName } from "../orderhistory/categoryUtils";
import { SupplierLogo } from "./SupplierLogo";


export type ColumnType = 'name' | 'base' | 'currency' | 'currencyHighlight' | 'currencyHighlightBlack' | 'blueNumber' | 'blueQuantity' | 'platform' | 'category' | 'subcategory' | 'boldPrice' | 'priceAlert' | 'unitPrice' | 'number';

export function isUndefined(value: any) : boolean {
  return value === undefined
}

// Helper function to render content based on columnType
export function renderByType(value: any, columnType: ColumnType, row?: any, onCategoryClick?: (productName: string, currentCategory?: string) => void, onNameClick?: (row: any) => void, onQuantityClick?: (row: any) => void, onPriceClick?: (row: any) => void, isDark: boolean = false): ReactNode {
    if (isUndefined(value)) return <></>
    switch (columnType) {
      case 'currency':
        const numValue = Number(value);
        if (isNaN(numValue)) return defaultReturn(value);
        return (
          <span className={cn(tertiaryText, smallTextSize, regularText)}>
            {currencyFormatter.format(numValue)}
          </span>
        );
      case 'currencyHighlight':
        const numValueHighlight = Number(value);
        if (isNaN(numValueHighlight)) return defaultReturn(value);
        return (
          <span className={cn(textDarkGreen, smallTextSize, boldText)}>
            {currencyFormatter.format(numValueHighlight)}
          </span>
        );
      case 'currencyHighlightBlack':
        const numValueHighlightBlack = Number(value);
        if (isNaN(numValueHighlightBlack)) return defaultReturn(value);
        return (
          <span className={cn(primaryText, smallTextSize, boldText)}>
            {currencyFormatter.format(numValueHighlightBlack)}
          </span>
        );
      case 'name':
        if (onNameClick) {
          return (
            <span
              className={cn(primaryText, smallTextSize, mediumText, "cursor-pointer hover:underline")}
              onClick={(e) => {
                e.stopPropagation();
                onNameClick(row);
              }}
              title="Click to copy Article ID"
            >
              {String(value)}
            </span>
          );
        }
        return <span className={cn(primaryText, smallTextSize, mediumText)}>{String(value)}</span>;
      case 'blueNumber':
        return (
          <span className={cn(
            textBlue,
            smallTextSize,
            semiBoldText,
            lightBlueBG,
            roundedLG,
            "px-2 py-1"
          )}>
            {
              Number.isInteger(value)
                ? String(value)
                : String(Number(value).toFixed(1))
            }
          </span>
        );

      case 'blueQuantity':
        if (onQuantityClick && row) {
          return (
            <span
              className={cn(
                textBlue,
                smallTextSize,
                semiBoldText,
                lightBlueBG,
                roundedLG,
                "px-2 py-1",
                "cursor-pointer hover:opacity-80 transition-opacity"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onQuantityClick(row);
              }}
              title="Click to edit quantity"
            >
              {value}
            </span>
          );
        }
        return (
          <span className={cn(
            textBlue,
            smallTextSize,
            semiBoldText,
            lightBlueBG,
            roundedLG,
            "px-2 py-1"
          )}>
            {
              value
            }
          </span>
        );
      case 'platform':
        const platformValue = typeof value === 'string' ? value : '';
        const unknownSupplierName = row?.unknownSupplierName;
        const isAnyInvoice = platformValue.toLowerCase() === 'any_invoice';

        // For ANY_INVOICE platforms, check if we have a logo for the specific supplier
        if (isAnyInvoice && unknownSupplierName && !hasAnyInvoiceLogo(unknownSupplierName)) {
          // No logo available - display the supplier name as text (truncated to 30 chars)
          const displayName = unknownSupplierName.length > 15
            ? `${unknownSupplierName.substring(0, 15)}...`
            : unknownSupplierName;
          return (
            <div className="flex justify-center items-center">
              <span
                className={cn(primaryText, smallTextSize, mediumText)}
                title={unknownSupplierName}
              >
                {displayName}
              </span>
            </div>
          );
        }

        // Display logo (either specific ANY_INVOICE logo or default platform logo)
        return (
          <div className="flex justify-center items-center">
            <SupplierLogo
              platform={value}
              unknownSupplierName={unknownSupplierName}
              size="large"
              alt={platformValue}
              className="object-cover"
            />
          </div>
        );
      case 'category':
        const categoryStyles = getCategoryStyles(value, isDark);
        const subCategories = row?.subCategories || [];
        const mainCategory = row?.mainCategory;
        
        const categoryBadge = (
          <span
            className={cn(
              smallTextSize,
              mediumText,
              roundedLG,
              "px-2 py-1 whitespace-nowrap flex-shrink-0",
              onCategoryClick && row?.name ? "cursor-pointer hover:opacity-80 transition-opacity" : ""
            )}
            style={categoryStyles}
            onClick={onCategoryClick && row?.name ? (e) => {
              e.stopPropagation();
              onCategoryClick(row.name, value);
            } : undefined}
            title={onCategoryClick && row?.name ? "Click to edit category" : undefined}
          >
            {formatCategoryName(value, mainCategory)}
          </span>
        );

        // If there are subcategories, display them horizontally scrollable
        if (subCategories.length > 0) {
          return (
            <div className="flex items-center gap-1 overflow-x-auto" style={{ maxWidth: '120px', width: '100%', whiteSpace: 'nowrap' }}>
              <div className="flex items-center gap-1 flex-shrink-0">
                {categoryBadge}
                {subCategories.map((subCat: string, idx: number) => {
                  const subCategoryStyles = getSubCategoryStyles(subCat, isDark);
                  return (
                    <span
                      key={idx}
                      className={cn(
                        smallTextSize,
                        mediumText,
                        roundedLG,
                        "px-2 py-1 whitespace-nowrap flex-shrink-0"
                      )}
                      style={subCategoryStyles}
                    >
                      {getSubCategoryDisplayName(subCat)}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        }

        // Wrap category badge in a container to respect max-width constraint
        return (
          <div className="overflow-x-auto whitespace-nowrap" style={{ maxWidth: '120px', width: '100%' }}>
            {categoryBadge}
          </div>
        );
      case 'subcategory':
        const subCategoryStyles = getSubCategoryStyles(value, isDark);
        return (
          <span
            className={cn(
              smallTextSize,
              mediumText,
              roundedLG,
              "px-2 py-1 whitespace-nowrap"
            )}
            style={subCategoryStyles}
          >
            {getSubCategoryDisplayName(value)}
          </span>
        );
      case 'boldPrice':
        const numpriceValue = Number(value);
        if (isNaN(numpriceValue)) return defaultReturn(value);
        if (onPriceClick && row) {
          return (
            <span
              className={cn(primaryText, smallTextSize, mediumText, "cursor-pointer hover:underline")}
              onClick={(e) => {
                e.stopPropagation();
                onPriceClick(row);
              }}
              title="Click to edit price"
            >
              {currencyFormatter.format(numpriceValue)}
            </span>
          );
        }
        return <span className={cn(primaryText, smallTextSize, mediumText)}>{currencyFormatter.format(numpriceValue)}</span>;
      case 'priceAlert':
        const isPositive = value >= 0;
        return <div className="shrink-0 flex items-center justify-center">
                  <div
                    className={cn(
                      "rounded-lg",
                      isPositive ? "bg-[#FEF3F2] dark:bg-red-900/20" : "bg-[#EDFCF5] dark:bg-[#0E8965]/20"
                    )}
                  >
                    <span
                      className={cn(
                        smallTextSize,
                        mediumText,
                        "whitespace-nowrap",
                        "px-2",
                        isPositive
                          ? "text-[#D92D20] dark:text-textRed-dark"
                          : "text-[#069A6E] dark:text-textGreen-dark"
                      )}
                    >
                      {(isPositive ? "+" : "") +
                        value.toLocaleString(undefined, {
                          maximumFractionDigits: 1,
                        }) +
                        "%"}
                    </span>
                  </div>
              </div>
      case 'unitPrice':
        return defaultReturn(value.displayText)
      case 'number':
        const numberValue = Number(value);
        if (isNaN(numberValue) || numberValue === 0) return defaultReturn("--");
        return defaultReturn(value.toFixed(2))
      case 'base':
      default:
        return defaultReturn(value);
    }
  }

  const defaultReturn = (value: any) => <span className={cn(tertiaryText, smallTextSize, regularText)}>{String(value)}</span>;
  